import {registerTranslateConfig, get, use} from 'lit-translate';
import {AvailableLanguages} from '@cbar/cbar-localizations';
import {mixinBehaviors} from '@polymer/polymer/lib/legacy/class.js';
import {dedupingMixin} from '@polymer/polymer/lib/utils/mixin.js';

{
  if (!window.Cbar) {
    window.Cbar = {};
  }

  /** Keeps track of the current site-wide language */
  let currentLanguage;
  let locales;
  const navigatorLanguage = navigator.language.toLowerCase();

  let attachedElements = [];
  let localesLoading = true;

  window.Cbar.LocalizeMixin = dedupingMixin(superClass => {
    return class extends mixinBehaviors([], superClass) {
      static get properties() {
        return {
          language: {
            type: String,
            value: navigatorLanguage,
            observer: "_onLanguageChanged",
            readOnly: true,
          },
          // Promise containing the JSON locales file
          locales: {
            type: Object,
            notify: true,
          },
          // Gets set to false when locales file has been loaded
          localesLoading: {
            type: Boolean,
            readOnly: true,
            value: true,
            notify: true,
          },
          localize: {
            // This is later re-computed to re-trigger translation
            type: Function,
          },
        }
      }

      connectedCallback() {
        super.connectedCallback();

        if (!locales) {
          this.loadData();
        } else {
          // Already loaded/loading
          this._setLocalesLoading(localesLoading);
          this.locales = locales;
        }

        // Keep an array of all attached elements so that we
        // may update the language site-wide.
        attachedElements.push(this);

        if (currentLanguage) {
          this._setLanguage(currentLanguage);
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        attachedElements.splice(attachedElements.indexOf(this), 1);
      }

      localize(str) {
        if (!str) {
          return '';
        }

        return get(str);
      }

      async loadData() {
        registerTranslateConfig({
          empty: key => key,
          loader: async lang => {
            let res;

            // TODO: THIS IS DUE TO AN ISSUE IN PASSPORT REQUIRING NON-DYNAMIC IMPORTS
            switch (lang) {
              case "de":
                res = await import(`@cbar/cbar-localizations/lngs/de.js`);
                break;
              case "en":
                res = await import(`@cbar/cbar-localizations/lngs/en.js`);
                break;
              case "es":
                res = await import(`@cbar/cbar-localizations/lngs/es.js`);
                break;
              case "fr":
                res = await import(`@cbar/cbar-localizations/lngs/fr.js`);
                break;
              case "pt":
                res = await import(`@cbar/cbar-localizations/lngs/pt.js`);
                break;
              case "sr":
                res = await import(`@cbar/cbar-localizations/lngs/sr.js`);
                break;
              default:
                res = await import(`@cbar/cbar-localizations/lngs/en.js`);
                break;
            }
            return res.default;
          }
        });

        // Set initial locale to browser language
        const browserLanguage = this._getLanguageFromBrowser();

        if (currentLanguage) {
          this.setLanguage(currentLanguage);
        } else {
          this.setLanguage(browserLanguage);
        }

        this._setLocalesLoading(false);
      }

      _getLanguageFromBrowser() {
        // Match the browser language to something in our locales
        const navigatorLanguage = navigator.language.toLowerCase();
        if (AvailableLanguages.indexOf(navigatorLanguage) > -1) {
          // easy
          return navigatorLanguage;
        }

        // Otherwise, check if we have a match for any of
        // the languages supplied in the navigator.languages array
        // IE11 does not have navigator.languages
        const navigatorLanguages = navigator.languages || [navigator.language];
        for (let language of navigatorLanguages) {
          language = language.toLowerCase();

          if (AvailableLanguages.indexOf(language) > -1) {
            return language;
          }
        }

        return 'en';
      }

      async _onLanguageChanged() {
        if (AvailableLanguages.indexOf(this.language) === -1) {
          return;
        }

        await use(this.language);
        // Update the localize method so that Polymer re-translates
        // any `localize()` calls
        this.localize = str => {
          if (!str) {
            return '';
          }

          return get(str);
        }

        this.dispatchEvent(new CustomEvent('language-changed', {
          bubbles: true,
          composed: true,
        }));
      }

      setLanguage(language) {
        if (language === "en-au") {
          var err = new Error();
          return console.log(err.stack);
        }


        // Update our "static" currentLanguage variable.
        currentLanguage = language;

        // Go through each attached element and update
        // the language.
        attachedElements.forEach(e => {
          e._setLanguage(language);
        });
      }
    };
  });
}
